import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineEye,
  AiOutlineStar,
  AiFillStar,
} from "react-icons/ai";
import { RxCross1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllProductsShop } from "../../redux/actions/product";
import { server, backend_url } from "../../server";
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from "../../uploadLimits";
import {
  hasLinkedSocialProfiles,
  SELLER_SOCIAL_REQUIRED_MESSAGE,
} from "../../utils/socialProfileLinks";
import Loader from "../Layout/Loader";
import MobileFriendlyModal, { MODAL_INPUT_CLASS } from "../Layout/MobileFriendlyModal";
import { categoriesData as fallbackCategoriesData } from "../../static/data";
import { fetchSiteOptions } from "../../redux/actions/siteOptions";

const initialFormState = {
  name: "",
  description: "",
  category: "",
  subcategory: "",
  categoryLink: "",
  categoryImage: "",
  brand: "",
  color: "",
  weaponPlatform: "",
  tags: "",
  originalPrice: "",
  discountPrice: "",
  stock: "",
  postageFees: "",
  dispatchTimeDays: "",
  deliveryMethod: "three_to_five_working_days",
};
const resolveCategoryImageSrc = (value) => {
  if (!value) return "";
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:")
  ) {
    return value;
  }
  const normalized = value.startsWith("/") ? value.slice(1) : value;
  return `${backend_url}${normalized}`;
};

const buildProductImageUrl = (value) => {
  if (!value) return "";
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  const normalized = value.startsWith("/") ? value.slice(1) : value;
  return `${backend_url}${normalized}`;
};

const LISTING_TABS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "sold", label: "Sold" },
  { id: "draft", label: "Draft" },
];

const isListingDraft = (item) => item?.status === "draft";

const isListingSold = (item) =>
  !isListingDraft(item) && Number(item?.stock ?? 0) < 1;

const isListingActive = (item) =>
  !isListingDraft(item) && Number(item?.stock ?? 0) >= 1;

const getListingBucket = (item) => {
  if (isListingDraft(item)) return "draft";
  if (isListingSold(item)) return "sold";
  return "active";
};

const LISTINGS_PAGE_SIZE = 10;

const tableBtnClass =
  "inline-flex items-center justify-center rounded border border-slate-200 bg-white px-2 py-1 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";

const ListingTableRow = React.memo(function ListingTableRow({
  row,
  relistingId,
  deletingId,
  onEdit,
  onDelete,
  onRelist,
}) {
  const rowClass = row.isSold
    ? "bg-slate-50 text-slate-500 hover:bg-slate-100"
    : "hover:bg-slate-50";

  const thumbnailSrc = row.thumbnail ? buildProductImageUrl(row.thumbnail) : "";

  return (
    <tr
      className={`cursor-pointer border-b border-slate-200 ${rowClass}`}
      onClick={() => onEdit(row.id)}
    >
      <td className="px-3 py-3">
        <div
          className={`relative flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded p-2 ${
            row.isSold ? "opacity-70" : ""
          }`}
        >
          {thumbnailSrc ? (
            <img
              src={thumbnailSrc}
              alt=""
              className={`h-full w-full object-contain ${row.isSold ? "grayscale" : ""}`}
            />
          ) : (
            <span className="text-xs text-slate-400">No img</span>
          )}
          {row.isSold ? (
            <span className="absolute bottom-0 left-0 right-0 bg-slate-800/80 py-0.5 text-center text-[9px] font-bold uppercase tracking-wide text-white">
              Sold
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-3">
        {row.isSold ? (
          <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
            Sold
          </span>
        ) : row.isDraft ? (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
            Draft
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
            Live
          </span>
        )}
      </td>
      <td className="px-3 py-3">
        <div className={`flex items-center gap-2 ${row.isSold ? "text-slate-500" : ""}`}>
          {row.isFeatured && !row.isSold ? (
            <AiFillStar
              size={18}
              className="shrink-0 text-amber-500"
              title={
                row.featuredUntilFormatted
                  ? `Featured until ${row.featuredUntilFormatted}`
                  : "Featured on homepage"
              }
            />
          ) : null}
          <span className="font-medium">{row.name}</span>
        </div>
      </td>
      <td className="px-3 py-3 whitespace-nowrap">{row.price}</td>
      <td className="px-3 py-3">
        <span className={row.isSold ? "font-semibold text-slate-500" : ""}>{row.Stock}</span>
      </td>
      <td className="px-3 py-3">{row.category}</td>
      <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
        {row.isDraft ? (
          <button
            type="button"
            className={tableBtnClass}
            onClick={() => onEdit(row.id)}
            title="Complete draft"
          >
            Edit
          </button>
        ) : row.isSold ? (
          <span className="text-xs text-slate-400">Hidden</span>
        ) : (
          <Link to={`/product/${row.id}`}>
            <button type="button" className={tableBtnClass} aria-label="Preview listing">
              <AiOutlineEye size={18} />
            </button>
          </Link>
        )}
      </td>
      <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center gap-1">
          {row.isSold ? (
            <button
              type="button"
              className={tableBtnClass}
              onClick={() => onRelist(row.id)}
              disabled={relistingId === row.id}
              title="Set stock and list again"
            >
              {relistingId === row.id ? "Listing…" : "Re-list"}
            </button>
          ) : null}
          {!row.isDraft && !row.isSold ? (
            <Link
              to={`/profile/feature-product?productId=${row.id}`}
              title={
                row.isFeatured
                  ? row.featuredUntilFormatted
                    ? `Featured until ${row.featuredUntilFormatted}`
                    : "Featured on homepage"
                  : "Feature on homepage (pay per week)"
              }
            >
              <button
                type="button"
                className={`${tableBtnClass} ${row.isFeatured ? "text-amber-500" : ""}`}
                aria-label={row.isFeatured ? "Featured listing" : "Feature listing"}
              >
                {row.isFeatured ? <AiFillStar size={18} /> : <AiOutlineStar size={18} />}
              </button>
            </Link>
          ) : null}
          <button
            type="button"
            className={tableBtnClass}
            onClick={() => onEdit(row.id)}
            aria-label="Edit listing"
          >
            <AiOutlineEdit size={18} />
          </button>
          <button
            type="button"
            className={tableBtnClass}
            onClick={() => onDelete(row.id)}
            disabled={deletingId === row.id}
            aria-label="Delete listing"
          >
            <AiOutlineDelete size={18} />
          </button>
        </div>
      </td>
    </tr>
  );
});

const AllProducts = () => {
  const dispatch = useDispatch();
  const { products, isLoading } = useSelector((state) => state.products);
  const { user } = useSelector((state) => state.user);
  const siteOptionsState = useSelector((state) => state.siteOptions);

  const catalogCategoriesRaw =
    siteOptionsState?.options?.global?.catalog?.categories || [];
  const hasSiteOptions = Boolean(siteOptionsState?.options?.global);
  const isSiteOptionsLoading = Boolean(siteOptionsState?.loading?.global);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [formState, setFormState] = useState(initialFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [imageBeingDeleted, setImageBeingDeleted] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [pendingImages, setPendingImages] = useState([]); // Local state for images before save
  const [imagesToAdd, setImagesToAdd] = useState([]); // New images to upload on save
  const [imagesToRemove, setImagesToRemove] = useState([]); // Images to delete on save
  const [listingTab, setListingTab] = useState("active");
  const [listingPage, setListingPage] = useState(0);
  const [relistingId, setRelistingId] = useState(null);
  const [relistModal, setRelistModal] = useState({ open: false, productId: null, stock: "1" });
  
  // Use ref to prevent multiple simultaneous API calls
  const isFetchingRef = useRef(false);
  const prevUserIdRef = useRef(null);
  const siteOptionsRequestedRef = useRef(false);

  const fallbackCategories = useMemo(() => {
    return (fallbackCategoriesData || []).map((item) => ({
      name: item.title,
      link:
        item.link || `/products?category=${encodeURIComponent(item.title || "")}`,
      image: item.image_Url || "",
    }));
  }, []);

  const fallbackImageMap = useMemo(() => {
    return fallbackCategories.reduce((acc, item) => {
      if (item.name) {
        acc[item.name] = item.image;
      }
      return acc;
    }, {});
  }, [fallbackCategories]);

  const availableCategories = useMemo(() => {
    if (Array.isArray(catalogCategoriesRaw) && catalogCategoriesRaw.length) {
      const normalized = catalogCategoriesRaw
        .filter((category) => category && category.name && category.isActive !== false)
        .sort(
          (a, b) => (typeof a.order === "number" ? a.order : 0) - (typeof b.order === "number" ? b.order : 0)
        )
        .map((category) => {
          const name = category.name?.trim() || "";
          const slugSource = category.slug?.trim() || name;
          const fallbackLink = slugSource
            ? `/products?category=${encodeURIComponent(slugSource)}`
            : "/products";

          return {
            name,
            link: category.link?.trim() || fallbackLink,
            image: category.image || category.icon || fallbackImageMap[name] || "",
            subcategories: (category.subcategories || [])
              .filter((sub) => sub && sub.name && sub.isActive !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((sub) => ({
                name: sub.name?.trim() || "",
                slug: sub.slug?.trim() || "",
              })),
          };
        });

      if (normalized.length) {
        return normalized;
      }
    }

    return fallbackCategories.map((item) => ({
      ...item,
      subcategories: [],
    }));
  }, [catalogCategoriesRaw, fallbackCategories, fallbackImageMap]);

  // Memoize selectedCategoryMissing to prevent unnecessary recalculations
  const selectedCategoryMissing = useMemo(() => {
    return formState.category &&
      !availableCategories.some((category) => category.name === formState.category);
  }, [formState.category, availableCategories]);

  const productFilters = siteOptionsState?.options?.global?.productFilters || {};
  const availableBrands = useMemo(() => {
    const list = productFilters.brands && Array.isArray(productFilters.brands) ? productFilters.brands.filter((b) => b && b.name) : [];
    return [...list].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [productFilters.brands]);
  const availableColors = useMemo(() => {
    const list = productFilters.colors && Array.isArray(productFilters.colors) ? productFilters.colors.filter((c) => c && c.name) : [];
    return [...list].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [productFilters.colors]);
  const availableWeaponPlatforms = useMemo(() => {
    const list = productFilters.weaponPlatforms && Array.isArray(productFilters.weaponPlatforms) ? productFilters.weaponPlatforms.filter((w) => w && w.name) : [];
    return [...list].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [productFilters.weaponPlatforms]);

  useEffect(() => {
    const currentUserId = user?._id;
    
    // Prevent duplicate calls if user ID hasn't changed or request is already in progress
    if (!currentUserId || currentUserId === prevUserIdRef.current || isFetchingRef.current) {
      return;
    }
    
    prevUserIdRef.current = currentUserId;
    isFetchingRef.current = true;
    
    dispatch(getAllProductsShop(currentUserId)).finally(() => {
      isFetchingRef.current = false;
    });
  }, [dispatch, user?._id]);

  useEffect(() => {
    if (hasSiteOptions || isSiteOptionsLoading || siteOptionsRequestedRef.current) {
      return;
    }
    siteOptionsRequestedRef.current = true;
    dispatch(fetchSiteOptions());
  }, [dispatch, hasSiteOptions, isSiteOptionsLoading]);

  const rows = useMemo(() => {
    return (products || []).map((item) => {
      const until = item.featuredUntil ? new Date(item.featuredUntil) : null;
      // Featured if promoted and (no end date or end date in future)
      const isFeatured = Boolean(item.isPromoted) && (!until || until > new Date());
      const featuredUntilFormatted = until ? until.toLocaleDateString(undefined, { dateStyle: "medium" }) : null;
      const isDraft = isListingDraft(item);
      const isSold = isListingSold(item);
      const listingBucket = getListingBucket(item);
      const thumbnail = item.images?.[0];
      return {
        id: item._id,
        thumbnail,
        name: item.name,
        price:
          typeof item.discountPrice === "number"
            ? `£${item.discountPrice.toFixed(2)}`
            : item.discountPrice,
        Stock: isSold ? "Sold" : item.stock,
        stockNumeric: Number(item.stock ?? 0),
        category: item.category || "",
        createdAt: item.createdAt ? new Date(item.createdAt).getTime() : 0,
        isPromoted: Boolean(item.isPromoted),
        featuredUntil: item.featuredUntil || null,
        isFeatured,
        featuredUntilFormatted,
        status: item.status || "active",
        isDraft,
        isSold,
        listingBucket,
        soldUnits: Number(item.sold_out ?? 0),
      };
    });
  }, [products]);

  const listingCounts = useMemo(() => {
    const counts = { all: rows.length, active: 0, sold: 0, draft: 0 };
    rows.forEach((row) => {
      if (row.listingBucket === "active") counts.active += 1;
      if (row.listingBucket === "sold") counts.sold += 1;
      if (row.listingBucket === "draft") counts.draft += 1;
    });
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (listingTab === "all") return rows;
    return rows.filter((row) => row.listingBucket === listingTab);
  }, [rows, listingTab]);

  useEffect(() => {
    setListingPage(0);
  }, [listingTab]);

  const listingPageCount = Math.max(1, Math.ceil(filteredRows.length / LISTINGS_PAGE_SIZE));

  useEffect(() => {
    if (listingPage > listingPageCount - 1) {
      setListingPage(Math.max(0, listingPageCount - 1));
    }
  }, [listingPage, listingPageCount]);

  const paginatedRows = useMemo(() => {
    const start = listingPage * LISTINGS_PAGE_SIZE;
    return filteredRows.slice(start, start + LISTINGS_PAGE_SIZE);
  }, [filteredRows, listingPage]);

  // Memoize form change handler to prevent unnecessary re-renders
  const handleFormChange = useCallback((field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Memoize category select handler to prevent unnecessary re-renders
  const handleCategorySelect = useCallback((value) => {
    const category = availableCategories.find((item) => item.name === value);
    setFormState((prev) => ({
      ...prev,
      category: value,
      subcategory: "", // Reset subcategory when main category changes
      categoryLink: category?.link || "",
      categoryImage: category?.image || "",
    }));
  }, [availableCategories]);

  const selectedCategoryData = useMemo(() => {
    return availableCategories.find((cat) => cat.name === formState.category);
  }, [availableCategories, formState.category]);

  const availableSubcategories = useMemo(() => {
    return selectedCategoryData?.subcategories || [];
  }, [selectedCategoryData]);

  const openEditModal = useCallback(
    (productId) => {
      const product = products.find((item) => item._id === productId);
      if (!product) return;

      setCurrentProduct(product);
      
      // Parse category - could be "Main Category" or "Main Category > Subcategory"
      const productCategory = product.category || "";
      let mainCategory = productCategory;
      let subcategory = "";
      
      if (productCategory.includes(" > ")) {
        const parts = productCategory.split(" > ");
        mainCategory = parts[0].trim();
        subcategory = parts[1]?.trim() || "";
      }
      
      const matchedCategory = availableCategories.find(
        (item) => item.name === mainCategory
      );
      
      setFormState({
        name: product.name || "",
        description: product.description || "",
        category: mainCategory,
        subcategory: subcategory,
        categoryLink: product.categoryLink || matchedCategory?.link || "",
        categoryImage: product.categoryImage || matchedCategory?.image || "",
        brand: product.brand || "",
        color: product.color || "",
        weaponPlatform: product.weaponPlatform || "",
        tags: product.tags || "",
        originalPrice:
          product.originalPrice !== undefined && product.originalPrice !== null
            ? product.originalPrice
            : "",
        discountPrice:
          product.discountPrice !== undefined && product.discountPrice !== null
            ? product.discountPrice
            : "",
        stock:
          product.stock !== undefined && product.stock !== null
            ? product.stock
            : "",
        postageFees:
          product.postageFees !== undefined && product.postageFees !== null
            ? product.postageFees
            : "",
        dispatchTimeDays:
          product.dispatchTimeDays !== undefined && product.dispatchTimeDays !== null
            ? product.dispatchTimeDays
            : 1,
        deliveryMethod: product.deliveryMethod || "three_to_five_working_days",
      });
      // Initialize pending images with current product images
      setPendingImages([...(product.images || [])]);
      setImagesToAdd([]);
      setImagesToRemove([]);
      setImageBeingDeleted(null);
      setIsModalOpen(true);
    },
    [products, availableCategories]
  );

  // Memoize reset modal state to prevent unnecessary re-renders
  const resetModalState = useCallback(() => {
    setIsModalOpen(false);
    setCurrentProduct(null);
    setFormState(initialFormState);
    setIsSaving(false);
    setImageBeingDeleted(null);
    setPendingImages([]);
    setImagesToAdd([]);
    setImagesToRemove([]);
  }, []);

  const refetchProducts = useCallback(() => {
    const currentUserId = user?._id;
    if (currentUserId && !isFetchingRef.current) {
      isFetchingRef.current = true;
      dispatch(getAllProductsShop(currentUserId)).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, user?._id]);

  // Memoize handleSave to prevent unnecessary re-renders (though it uses many dependencies)
  const handleSave = useCallback(async (event, { asDraft = false } = {}) => {
    event.preventDefault();
    if (!currentProduct) return;

    const hasLinkedSocial = hasLinkedSocialProfiles(user?.linkedSocialProfiles);
    const wantsPublish = currentProduct.status === "draft" && !asDraft;
    const keepAsDraft = asDraft || (wantsPublish && !hasLinkedSocial);
    const publishingDraft = wantsPublish && hasLinkedSocial;

    try {
      setIsSaving(true);

      if (!keepAsDraft) {
        if (!formState.category) {
          throw new Error("Please choose a category.");
        }
        if (!availableSubcategories.length) {
          throw new Error("This category has no subcategories configured. Please choose a different category.");
        }
        if (!formState.subcategory) {
          throw new Error("Please choose a subcategory.");
        }
      }

      const parseNumberField = (value, fieldName, { required = false } = {}) => {
        if (value === "" || value === null || value === undefined) {
          if (required) {
            throw new Error(`${fieldName} is required.`);
          }
          return undefined;
        }
        const parsed = Number(value);
        if (Number.isNaN(parsed)) {
          throw new Error(`${fieldName} must be a valid number.`);
        }
        return parsed;
      };

      // Store category as "Main Category > Subcategory" if subcategory is selected, otherwise just main category
      const finalCategory = formState.subcategory 
        ? `${formState.category} > ${formState.subcategory}` 
        : formState.category;

      const payload = {
        name: formState.name || (keepAsDraft ? "Draft listing" : formState.name),
        description: formState.description || (keepAsDraft ? "<p></p>" : formState.description),
        category: finalCategory || (keepAsDraft ? "Uncategorised" : finalCategory),
        brand: formState.brand || "",
        color: formState.color || "",
        weaponPlatform: formState.weaponPlatform || "",
        tags: formState.tags,
        originalPrice: parseNumberField(formState.originalPrice, "Old price"),
        discountPrice: parseNumberField(formState.discountPrice, "New price", {
          required: !keepAsDraft,
        }),
        stock: parseNumberField(formState.stock, "Stock", { required: !keepAsDraft }),
        postageFees: parseNumberField(formState.postageFees, "Postage fees", {
          required: !keepAsDraft,
        }),
        dispatchTimeDays: parseNumberField(formState.dispatchTimeDays, "Dispatch time", {
          required: !keepAsDraft,
        }),
        deliveryMethod: formState.deliveryMethod,
        ...(currentProduct.status === "draft"
          ? { status: keepAsDraft ? "draft" : "active" }
          : {}),
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === "") {
          delete payload[key];
        }
      });

      // Save product details first
      const { data: saveData } = await axios.put(
        `${server}/product/update-shop-product/${currentProduct._id}`,
        payload,
        { withCredentials: true }
      );
      const socialBlocked = saveData?.code === "SOCIAL_REQUIRED" || (wantsPublish && saveData?.published === false);

      // Handle image deletions
      for (const filename of imagesToRemove) {
        try {
          await axios.delete(
            `${server}/product/delete-shop-product-image/${currentProduct._id}`,
            {
              params: { filename },
              withCredentials: true,
            }
          );
        } catch (error) {
          // Failed to delete image
        }
      }

      // Handle image uploads and get new filenames
      let uploadedFilenames = [];
      if (imagesToAdd.length > 0) {
        const formData = new FormData();
        imagesToAdd.forEach((img) => {
          if (img.file) {
            formData.append("images", img.file);
          }
        });

        try {
          const uploadResponse = await axios.post(
            `${server}/product/add-shop-product-images/${currentProduct._id}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
              withCredentials: true,
            }
          );
          // Get newly uploaded filenames from response
          if (uploadResponse.data?.images) {
            const currentImagesBeforeUpload = (currentProduct.images || []).filter(
              (img) => !imagesToRemove.includes(img) && !pendingImages.some((p) => p === img)
            );
            uploadedFilenames = uploadResponse.data.images.filter(
              (img) => !currentImagesBeforeUpload.includes(img)
            );
          }
        } catch (error) {
          toast.error(
            error?.response?.data?.message ||
              "Product updated but failed to upload some images"
          );
        }
      }

      // Build final ordered images array from pendingImages
      // Map blob: URLs to newly uploaded filenames and keep existing filenames
      let uploadedIndex = 0;
      const finalImagesOrder = pendingImages
        .map((img) => {
          // If it's a blob URL (new image), replace with uploaded filename
          if (typeof img === 'string' && img.startsWith('blob:')) {
            return uploadedFilenames[uploadedIndex++];
          }
          // Otherwise it's an existing filename
          return img;
        })
        .filter(Boolean); // Remove any undefined values

      // Update product with final ordered images array
      if (finalImagesOrder.length > 0 || imagesToRemove.length > 0 || imagesToAdd.length > 0) {
        try {
          await axios.put(
            `${server}/product/update-shop-product/${currentProduct._id}`,
            { images: finalImagesOrder },
            { withCredentials: true }
          );
        } catch (error) {
          // Failed to update image order
          // Don't fail the entire operation if order update fails
        }
      }

      if (socialBlocked || (wantsPublish && !hasLinkedSocial)) {
        toast.info(
          saveData?.message ||
            `${SELLER_SOCIAL_REQUIRED_MESSAGE} Your listing was saved as a draft.`
        );
      } else {
        toast.success(
          publishingDraft
            ? "Listing published"
            : asDraft || keepAsDraft
            ? "Draft saved"
            : "Product updated"
        );
      }
      resetModalState();
      if (typeof window !== "undefined" && window.requestIdleCallback) {
        window.requestIdleCallback(refetchProducts, { timeout: 200 });
      } else {
        setTimeout(refetchProducts, 200);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to save product";
      toast.error(message);
      if (currentProduct?._id) {
        try {
          await axios.put(
            `${server}/product/update-shop-product/${currentProduct._id}`,
            {
              name: formState.name || "Draft listing",
              description: formState.description || "<p></p>",
              status: "draft",
            },
            { withCredentials: true }
          );
          toast.info("Your listing was kept as a draft.");
        } catch {
          // ignore secondary save failure
        }
      }
      setIsSaving(false);
    }
  }, [currentProduct, formState, imagesToRemove, imagesToAdd, pendingImages, availableSubcategories.length, refetchProducts, resetModalState, user?.linkedSocialProfiles]);

  // Memoize image delete handler to prevent unnecessary re-renders
  const handleDeleteImage = useCallback((imageIdentifier) => {
    if (!imageIdentifier) return;

    // Check if it's a new image (preview URL) or existing image (filename)
    const isNewImage = typeof imageIdentifier === 'string' && imageIdentifier.startsWith('blob:');
    
    // Remove from pending images (local state only)
    setPendingImages((prev) => prev.filter((img) => img !== imageIdentifier));
    
    if (isNewImage) {
      // Remove from imagesToAdd if it was a new image
      setImagesToAdd((prev) => {
        const updated = prev.filter((img) => img.preview !== imageIdentifier);
        // Clean up the object URL
        URL.revokeObjectURL(imageIdentifier);
        return updated;
      });
    } else {
      // Track for deletion on save (existing image)
      setImagesToRemove((prev) => {
        if (!prev.includes(imageIdentifier)) {
          return [...prev, imageIdentifier];
        }
        return prev;
      });
    }
  }, []);

  // Memoize add images handler to prevent unnecessary re-renders
  const handleAddImages = useCallback((event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (!files.length) {
      input.value = "";
      return;
    }

    // Validate file sizes
    const oversizedFiles = files.filter(
      (file) => file.size > IMAGE_UPLOAD_MAX_BYTES
    );
    if (oversizedFiles.length > 0) {
      toast.error(
        `One or more images exceed the ${IMAGE_UPLOAD_MAX_MB}MB size limit. Maximum upload size is ${IMAGE_UPLOAD_MAX_MB}MB per image.`
      );
      input.value = "";
      return;
    }

    // Add to local state only (preview)
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isNew: true,
    }));

    setImagesToAdd((prev) => [...prev, ...newImages]);
    // Add preview URLs to pending images
    setPendingImages((prev) => [...prev, ...newImages.map(img => img.preview)]);
    
    input.value = "";
  }, []);

  // Memoize drag handlers to prevent unnecessary re-renders of image items
  const handleDragStart = useCallback((index) => (e) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.currentTarget.style.opacity = '0.5';
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.currentTarget.style.opacity = '1';
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.style.border = '2px dashed #38513b';
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.currentTarget.style.border = '';
  }, []);

  const handleImageDrop = useCallback((index) => (e) => {
    e.preventDefault();
    e.currentTarget.style.border = '';
    const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
    setPendingImages((prev) => {
      const newImages = [...prev];
      const [removed] = newImages.splice(draggedIndex, 1);
      newImages.splice(index, 0, removed);
      return newImages;
    });
  }, []);

  const openRelistModal = useCallback((productId) => {
    setRelistModal({ open: true, productId, stock: "1" });
  }, []);

  const closeRelistModal = useCallback(() => {
    setRelistModal({ open: false, productId: null, stock: "1" });
  }, []);

  const submitRelist = useCallback(async () => {
    const productId = relistModal.productId;
    if (!productId) return;

    const stock = Number(relistModal.stock);
    if (!Number.isFinite(stock) || stock < 1) {
      toast.error("Enter a stock quantity of at least 1.");
      return;
    }

    try {
      setRelistingId(productId);
      await axios.put(
        `${server}/product/update-shop-product/${productId}`,
        { stock, status: "active" },
        { withCredentials: true }
      );
      toast.success("Listing is live again.");
      closeRelistModal();
      refetchProducts();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to re-list this product."
      );
    } finally {
      setRelistingId(null);
    }
  }, [relistModal.productId, relistModal.stock, closeRelistModal, refetchProducts]);

  const handleRelist = useCallback(
    (productId) => {
      openRelistModal(productId);
    },
    [openRelistModal]
  );

  const handleDelete = useCallback(async (productId) => {
    const confirm = window.confirm(
      "Are you sure you want to delete this product? This action cannot be undone."
    );
    if (!confirm) return;

    try {
      setDeletingId(productId);
      await axios.delete(`${server}/product/delete-shop-product/${productId}`, {
        withCredentials: true,
      });
      toast.success("Product deleted");
      refetchProducts();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to delete the product. Please try again."
      );
    } finally {
      setDeletingId(null);
    }
  }, [refetchProducts]);

  const isInitialLoad = isLoading && !(products && products.length);

  if (isInitialLoad) {
    return <Loader />;
  }

  return (
    <>
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <h2 className="text-xl font-semibold text-slate-900">My Listings</h2>
          <p className="text-sm text-slate-600 mt-1">
            Manage live listings, sold items, and drafts. Sold items stay here until you re-list or delete them.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {LISTING_TABS.map((tab) => {
              const count = listingCounts[tab.id] ?? 0;
              const isActive = listingTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setListingTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "bg-[#38513b] text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-xs ${
                      isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-4">
          {filteredRows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
              <p className="text-sm font-medium text-slate-700">
                {listingTab === "sold"
                  ? "No sold listings yet."
                  : listingTab === "draft"
                  ? "No drafts saved."
                  : listingTab === "active"
                  ? "No active listings. Publish a draft or re-list a sold item."
                  : "You have no listings yet."}
              </p>
            </div>
          ) : (
            <>
              <div
                className="overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]"
                role="region"
                aria-label="Scroll sideways to see all columns (price, actions, etc.)"
              >
                <table className="min-w-[720px] w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-800 text-left text-xs font-bold uppercase tracking-wide text-white">
                      <th className="px-3 py-3">Image</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3">Name</th>
                      <th className="px-3 py-3">Price</th>
                      <th className="px-3 py-3">Stock</th>
                      <th className="px-3 py-3">Category</th>
                      <th className="px-3 py-3">Preview</th>
                      <th className="px-3 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row) => (
                      <ListingTableRow
                        key={row.id}
                        row={row}
                        relistingId={relistingId}
                        deletingId={deletingId}
                        onEdit={openEditModal}
                        onDelete={handleDelete}
                        onRelist={handleRelist}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredRows.length > LISTINGS_PAGE_SIZE ? (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm text-slate-600">
                  <p>
                    Showing {listingPage * LISTINGS_PAGE_SIZE + 1}–
                    {Math.min((listingPage + 1) * LISTINGS_PAGE_SIZE, filteredRows.length)} of{" "}
                    {filteredRows.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className={`${tableBtnClass} px-3`}
                      onClick={() => setListingPage((prev) => Math.max(0, prev - 1))}
                      disabled={listingPage === 0}
                    >
                      Previous
                    </button>
                    <span className="px-1">
                      Page {listingPage + 1} of {listingPageCount}
                    </span>
                    <button
                      type="button"
                      className={`${tableBtnClass} px-3`}
                      onClick={() =>
                        setListingPage((prev) => Math.min(listingPageCount - 1, prev + 1))
                      }
                      disabled={listingPage >= listingPageCount - 1}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div 
          className="fixed inset-0 z-[100000] flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              resetModalState();
            }
          }}
        >
          <div 
            className="w-full max-w-4xl bg-white rounded-2xl shadow-xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-slate-900">Edit Product</h2>
              <button
                type="button"
                className="p-2 hover:bg-slate-100 rounded-lg transition cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  resetModalState();
                }}
              >
                <RxCross1 size={22} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formState.name}
                    onChange={(e) => handleFormChange("name", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                    required
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={formState.category || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleCategorySelect(value);
                        handleFormChange("subcategory", "");
                      }}
                      className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                      required
                    >
                      <option value="" disabled>
                        Select category
                      </option>
                      {availableCategories.map((cat) => (
                        <option key={cat.name} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                      {selectedCategoryMissing && (
                        <option value={formState.category}>{formState.category} (legacy)</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Subcategory *
                    </label>
                    <select
                      value={formState.subcategory || ""}
                      onChange={(e) => handleFormChange("subcategory", e.target.value)}
                      disabled={!formState.category || availableSubcategories.length === 0}
                      required
                      className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20 disabled:bg-slate-50 disabled:text-slate-500"
                    >
                      <option value="">
                        {availableSubcategories.length === 0 ? "No subcategories" : "Select subcategory (optional)"}
                      </option>
                      {availableSubcategories
                        .slice()
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((sub) => (
                          <option key={sub.name} value={sub.name}>
                            {sub.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                {formState.categoryImage && (
                    <div className="flex items-center gap-3 mt-2">
                      <img
                        src={resolveCategoryImageSrc(formState.categoryImage)}
                        alt="Category"
                        className="h-12 w-12 object-contain rounded border"
                      />
                      {formState.categoryLink && (
                        <a
                          href={formState.categoryLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 underline"
                        >
                          View category link
                        </a>
                      )}
                    </div>
                  )}
                  {selectedCategoryMissing && (
                    <p className="mt-1 text-xs text-amber-600">
                      This product uses a legacy category. Choose one of the header categories to update it.
                    </p>
                  )}
                  {!availableCategories.length && (
                    <p className="mt-1 text-xs text-red-500">
                      No categories available. Contact the admin to configure header categories.
                    </p>
                  )}
                </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Brand</label>
                  <select
                    value={formState.brand || ""}
                    onChange={(e) => handleFormChange("brand", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                  >
                    <option value="">—</option>
                    {availableBrands.map((b) => (
                      <option key={b.name} value={b.name}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Primary colour</label>
                  <select
                    value={formState.color || ""}
                    onChange={(e) => handleFormChange("color", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                  >
                    <option value="">—</option>
                    {availableColors.map((c) => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Weapon platform</label>
                  <select
                    value={formState.weaponPlatform || ""}
                    onChange={(e) => handleFormChange("weaponPlatform", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                  >
                    <option value="">—</option>
                    {availableWeaponPlatforms.map((w) => (
                      <option key={w.name} value={w.name}>{w.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  value={formState.tags}
                  onChange={(e) => handleFormChange("tags", e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                  placeholder="Optional metadata"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description *
                </label>
                <div className="border border-slate-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={formState.description}
                    onChange={(value) => handleFormChange("description", value)}
                    className="bg-white"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Use the toolbar to format the product description. Content saves as HTML.
                </p>
              </div>

              {pendingImages.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Product Images <span className="text-xs font-normal text-slate-500">(Drag to reorder - first image is featured)</span>
                  </label>
                  <div className="flex flex-wrap gap-3" id="image-container">
                    {pendingImages.map((image, index) => {
                      // Check if it's a new image (preview) or existing
                      const isNewImage = typeof image === 'string' && image.startsWith('blob:');
                      const imageUrl = isNewImage 
                        ? image 
                        : buildProductImageUrl(image);
                      const isFirstImage = index === 0;
                      
                      return (
                        <div
                          key={`${image}-${index}`}
                          draggable
                          onDragStart={handleDragStart(index)}
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleImageDrop(index)}
                          className={`relative group cursor-move ${isFirstImage ? 'ring-2 ring-[#38513b] ring-offset-2' : ''}`}
                        >
                          <img
                            src={imageUrl}
                            alt="Product"
                            className="h-20 w-20 object-contain rounded border"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                          <button
                            type="button"
                            className="absolute top-1 right-1 bg-black/70 text-white text-xs px-2 py-1 rounded hover:bg-black/90"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(image);
                            }}
                          >
                            Remove
                          </button>
                          {isFirstImage && (
                            <span className="absolute bottom-1 left-1 bg-[#38513b] text-white text-xs px-2 py-1 rounded-md">Featured</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Changes will be saved when you click "Save changes". Drag images to reorder them.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Add Images
                </label>
                <p className="text-xs text-slate-500 mb-2">Maximum upload size: {IMAGE_UPLOAD_MAX_MB}MB per image</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    e.preventDefault();
                    handleAddImages(e);
                  }}
                  disabled={isSaving}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#38513b] file:text-white hover:file:bg-[#2f4232]"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Upload additional gallery images for this product.
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Recommended size: 800x800px or 1000x1000px (square images work best)
                </p>
              </div>

              {/* Feature on homepage */}
              {currentProduct && (
                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Feature on homepage
                  </label>
                  {(() => {
                    const until = currentProduct.featuredUntil ? new Date(currentProduct.featuredUntil) : null;
                    const isCurrentlyFeatured = Boolean(currentProduct.isPromoted && until && until > new Date());
                    const untilStr = until && until > new Date() ? until.toLocaleDateString(undefined, { dateStyle: "medium" }) : null;
                    return (
                      <>
                        {isCurrentlyFeatured ? (
                          <>
                            <button
                              type="button"
                              disabled
                              className="mb-2 inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 opacity-90 cursor-not-allowed"
                            >
                              <AiFillStar size={18} /> Featured
                            </button>
                            <p className="text-sm text-slate-600">
                              Featured until <strong>{untilStr}</strong>. It will automatically stop showing in Featured Products after that date.
                            </p>
                            <Link
                              to={`/profile/feature-product?productId=${currentProduct._id}`}
                              className="mt-2 inline-block text-sm text-[#38513b] font-medium hover:underline"
                            >
                              View feature details →
                            </Link>
                          </>
                        ) : (
                          <>
                            <Link
                              to={`/profile/feature-product?productId=${currentProduct._id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-2 rounded-lg bg-[#38513b] px-4 py-2 text-sm font-medium text-white hover:bg-[#2f4232] transition"
                            >
                              <AiOutlineStar size={18} /> Feature on homepage
                            </Link>
                            <p className="mt-2 text-xs text-slate-500">
                              Pay per week to show this product in the homepage Featured section. Free featured options can be applied without payment.
                            </p>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Old Price
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formState.originalPrice}
                    onChange={(e) => handleFormChange("originalPrice", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    New Price *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formState.discountPrice}
                    onChange={(e) => handleFormChange("discountPrice", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Postage Fees *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formState.postageFees}
                    onChange={(e) => handleFormChange("postageFees", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Stock *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formState.stock}
                    onChange={(e) => handleFormChange("stock", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                    required
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Dispatch time (days) *
                  </label>
                  <select
                    value={formState.dispatchTimeDays}
                    onChange={(e) => handleFormChange("dispatchTimeDays", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                  >
                    {[1, 2, 3, 4, 5].map((d) => (
                      <option key={d} value={d}>
                        {d} day{d === 1 ? "" : "s"}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Delivery method *
                  </label>
                  <select
                    value={formState.deliveryMethod}
                    onChange={(e) => handleFormChange("deliveryMethod", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                  >
                    <option value="next_day">Next day</option>
                    <option value="two_working_days">2 Working Days</option>
                    <option value="three_to_five_working_days">3-5 business working days</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={resetModalState}
                  className="px-6 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                {currentProduct?.status === "draft" ? (
                  <>
                    <button
                      type="button"
                      onClick={(event) => handleSave(event, { asDraft: true })}
                      className="px-6 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-800 font-medium hover:bg-slate-50 transition disabled:opacity-50"
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving..." : "Save draft"}
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2.5 rounded-lg bg-[#38513b] text-white font-medium hover:bg-[#2f4232] transition disabled:opacity-50"
                      disabled={isSaving}
                    >
                      {isSaving ? "Publishing..." : "Publish listing"}
                    </button>
                  </>
                ) : (
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-lg bg-[#38513b] text-white font-medium hover:bg-[#2f4232] transition disabled:opacity-50"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {relistModal.open && (
        <MobileFriendlyModal onClose={closeRelistModal} zClass="z-[100001]">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Re-list item</h3>
          <p className="text-sm text-slate-600 mb-4">
            Set how many units you want to list again. The item will appear under Active listings when stock is saved.
          </p>
          <label className="block text-sm font-medium text-slate-700 mb-1">Stock quantity</label>
          <input
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            value={relistModal.stock}
            onChange={(e) =>
              setRelistModal((prev) => ({ ...prev, stock: e.target.value }))
            }
            className={MODAL_INPUT_CLASS}
          />
          <div className="mt-4 flex gap-3 justify-end">
            <button
              type="button"
              onClick={closeRelistModal}
              className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              disabled={Boolean(relistingId)}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitRelist}
              disabled={Boolean(relistingId)}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-[#38513b] px-4 text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-50"
            >
              {relistingId ? "Listing…" : "Re-list"}
            </button>
          </div>
        </MobileFriendlyModal>
      )}
    </>
  );
};

export default AllProducts;
